import { type Node } from "prosemirror-model";
import { type Command } from "prosemirror-state";

import { schema } from "@moment/prosemirror-schema";
import { DEFAULT_CELL_ID, createCellID } from "@moment/prosemirror-utils";

import { getPreferredCodeBlockLanguage } from "../nodeViews/codeBlock/codeBlockLanguagePreference";
import {
	type ConvertNodeCommandHelper,
	type ConvertNodeOptions,
	makeConvertNodeCommandHelper,
} from "./makeConvertNodeCommandHelper";

export function convertNodesInSelection(convertTo: ConvertNodeOptions): Command {
	return function (state, dispatch) {
		if (!dispatch) {
			return false;
		}

		let canConvert = true;
		const helpers: ConvertNodeCommandHelper[] = [];
		const nodesToConvert: { node: Node; pos: number }[] = [];

		for (let i = 0; i < state.selection.ranges.length && canConvert; i++) {
			const selection = state.selection.ranges[i];
			if (!selection) {
				return false;
			}

			const {
				$from: { pos: from },
				$to: { pos: to },
			} = selection;
			state.doc.nodesBetween(from, to, (node, pos, parent) => {
				// Only convert block nodes that are direct children of the document.
				if (!node.isBlock || !node.firstChild || parent !== state.doc) {
					return false;
				}
				const helper = makeConvertNodeCommandHelper(node, pos, convertTo);
				if (helper.conversionType === "invalid") {
					canConvert = false;
				}
				helpers.push(helper);
				nodesToConvert.push({ node, pos });
			});

			if (!canConvert) {
				return false;
			}
		}

		const tr = state.tr;

		// Converting multiple contiguous nodes to a `code_block` should create a single
		// `code_block` node, not several `code_block` nodes.
		if (canConvert && convertTo.nodeType === "code_block") {
			//
			// Sometimes people have "soft returns" in their text blocks, like this:
			//
			//    hello\
			//    world
			//
			// We want to simply turn these into newlines inside the `code_block`, as so:
			//
			//    ```
			//    hello
			//    world
			//    ```
			//
			const extractTextWithBreaks = (node: Node): string => {
				// Just output `textContent` for non-paragraph nodes. Only need to get fancy for
				// paragraphs with hard breaks.
				if (node.type !== schema.nodes.paragraph) {
					return node.textContent;
				}

				let text = "";
				node.descendants((child) => {
					if (child.isText) {
						text += child.text;
					} else if (child.type === schema.nodes.hard_break) {
						text += "\n";
					}
				});
				return text;
			};

			// Collect text content from all nodes, preserving hard breaks as newlines.
			const textContent = nodesToConvert.map((n) => extractTextWithBreaks(n.node)).join("\n");

			if (nodesToConvert.length === 0) {
				return false;
			}

			// Get the range from the first to the last node.
			const firstPos = nodesToConvert[0].pos;
			const lastNode = nodesToConvert[nodesToConvert.length - 1];
			const lastPos = lastNode.pos + lastNode.node.nodeSize;

			// Generate or reuse cell ID.
			const cellId =
				nodesToConvert[0].node.attrs["id"] === DEFAULT_CELL_ID
					? createCellID()
					: nodesToConvert[0].node.attrs["id"];

			// Create a single code_block with all the content.
			const codeBlock = schema.nodes.code_block.create(
				{ id: cellId, placeholderText: "", language: getPreferredCodeBlockLanguage() },
				schema.text(textContent)
			);

			// Replace all selected nodes with a single code_block
			tr.replaceWith(tr.mapping.map(firstPos), tr.mapping.map(lastPos), codeBlock);

			dispatch(tr.scrollIntoView());
			return true;
		}

		// Default behavior: convert each node individually
		for (const helper of helpers) {
			if (helper.conversionType === "valid") {
				helper.addTransformToTransaction(tr);
			}
		}

		dispatch(tr.scrollIntoView());

		return true;
	};
}
